import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import styled from 'styled-components';

import { Dropdown, Skeleton } from '@soluto-private/mx-asurion-ui-react';

import { type DeviceOption } from '@service-initiation/shared-clients-tech-care-services';
import { useInViewAnalytics } from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  deviceColorOptionIdsAsyncAtom,
  deviceDetailsAsyncAtom,
  deviceDetailsPageConfirmedAtom,
  store,
  useDeviceColorOptionIdsAsyncAtom,
  useDeviceColorOptionIdsAtom,
  useDeviceDetailsAsyncAtom,
  useDeviceDetailsAtom,
  useSelectedDeviceTypeAsyncAtom,
} from '@service-initiation/tech-care-data-access-store';
import { type DeviceDetails } from '@service-initiation/tech-care-data-access-tech-care-services';
import { ClaimsOffRamp } from '@service-initiation/tech-care-ui-claims-off-ramp';
import {
  NavigationButtonsContainer,
  type NavigationButtonsContainerProps,
} from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { completedDeviceDetails } from './completedDeviceDetails';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const InputsContainer = styled.div`
  text-align: left;
  flex-grow: 1;
`;

const DropdownContainer = styled.div`
  margin-top: 16px;
`;

const SkeletonContainer = styled.div`
  margin-top: 16px;
`;

const TEXTS = TranslationEntries.Repairs.DeviceDetails;

const getDropdownsRecursive = (device: DeviceDetails): DeviceDetails[] => {
  return device.parent
    ? [...getDropdownsRecursive(device.parent), device]
    : [device];
};

export function DeviceDetailsPage() {
  const { loading: loadingDeviceDetails, value: deviceDetails } =
    useDeviceDetailsAsyncAtom();
  const { loading: loadingSelectedDeviceType, value: selectedDeviceType } =
    useSelectedDeviceTypeAsyncAtom();

  const [, setDeviceColorOptionIds] = useDeviceColorOptionIdsAtom();
  const [, setDeviceDetails] = useDeviceDetailsAtom();

  const [{ loading: loadingNextStep }, goToNextStep] = useGoToNextStep();

  const [selectedDevice, setSelectedDevice] = useState<
    DeviceDetails | undefined
  >(deviceDetails);

  const dropdowns = selectedDevice ? getDropdownsRecursive(selectedDevice) : [];
  const [deviceColorOptions, setDeviceColorOptions] = useState<
    DeviceOption[] | undefined
  >(deviceDetails?.colorOptions);

  const [selectedDeviceColorOption, setSelectedDeviceColorOption] = useState<
    DeviceOption | undefined
  >(deviceDetails?.selectedDeviceColorOption);

  const { loading: loadingColorOptions } = useDeviceColorOptionIdsAsyncAtom();

  const { t } = useTranslation();

  const { ref: elementRef } = useInViewAnalytics('DeviceDetailsPage', {
    ...(selectedDevice && {
      deviceId: selectedDevice.id.toString(),
      deviceName: selectedDevice.name,
    }),
  });

  const [{ loading: loadingHandleDeviceChange }, handleDeviceChange] =
    useAsyncFn(async (deviceDetails: DeviceDetails) => {
      setSelectedDevice(deviceDetails);
      setDeviceColorOptions(undefined);
      setSelectedDeviceColorOption(undefined);
      setDeviceDetails(deviceDetails);

      const selectedDeviceDetails = await store.get(deviceDetailsAsyncAtom);

      let colorOptions: DeviceOption[] | undefined = [];
      if (selectedDeviceDetails?.isEligibleForColorOptions) {
        colorOptions = await store.get(deviceColorOptionIdsAsyncAtom);

        setDeviceColorOptions(colorOptions);
      }

      setSelectedDevice(selectedDeviceDetails);
      setDeviceDetails(selectedDeviceDetails);
      setDeviceColorOptions(colorOptions);
    }, []);

  const handleDeviceColorOptionChange = useCallback(
    (selectedDeviceColorOption?: DeviceOption) => {
      setSelectedDeviceColorOption(selectedDeviceColorOption);

      if (selectedDevice) {
        setDeviceDetails(selectedDevice);
        setDeviceColorOptionIds(selectedDeviceColorOption?.option_ids || []);
      }
    },
    [selectedDevice, setDeviceDetails, setDeviceColorOptionIds]
  );

  const navigateButtonProps: NavigationButtonsContainerProps = {
    disableContinueButton: !completedDeviceDetails(deviceDetails),
    onContinueButtonClick: () => {
      store.set(deviceDetailsPageConfirmedAtom, true);
      goToNextStep();
    },
    pageName: 'DeviceDetails',
    showBackButton: true,
  };

  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          {!loadingSelectedDeviceType && !loadingNextStep ? (
            <Container
              data-testid="device-details"
              id="DeviceDetails"
              ref={elementRef}
            >
              <StepTitle
                subtitle={t(TEXTS.SubTitle)}
                title={t(TEXTS.Title, {
                  deviceTypeName: selectedDeviceType?.name.toLowerCase(),
                })}
              />
              <InputsContainer>
                {dropdowns
                  .filter((device) => !device.isBottomLevel)
                  .map((dropdown, index) => (
                    <DropdownContainer key={dropdown.id}>
                      <Dropdown
                        data-testid={`dropdown-${
                          index === 0 ? 'brand' : `model-${index}`
                        }`}
                        disabled={
                          (loadingDeviceDetails || loadingHandleDeviceChange) &&
                          index === dropdowns.length - 1
                        }
                        label={
                          index === 0
                            ? t(TEXTS.SelectLabel.Brand)
                            : t(TEXTS.SelectLabel.Model)
                        }
                        onChange={(event) => {
                          const { value } = event.target as HTMLSelectElement;
                          const nextSelection = dropdown.children?.find(
                            (child) => child.id === Number(value)
                          );
                          if (nextSelection) {
                            handleDeviceChange({
                              ...nextSelection,
                              parent: dropdown,
                            });
                          }
                        }}
                        options={
                          dropdown.children?.map((child) => ({
                            name: child.name,
                            value: child.id.toString(),
                          })) || []
                        }
                        value={
                          index + 1 < dropdowns.length
                            ? dropdowns[index + 1]?.id?.toString()
                            : undefined
                        }
                      />
                    </DropdownContainer>
                  ))}

                {loadingColorOptions ||
                (!deviceColorOptions &&
                  selectedDevice &&
                  selectedDevice.isEligibleForColorOptions) ? (
                  <SkeletonContainer>
                    <Skeleton height="56px" />
                  </SkeletonContainer>
                ) : (
                  deviceColorOptions &&
                  deviceColorOptions.length > 0 && (
                    <DropdownContainer>
                      <Dropdown
                        data-testid={`dropdown-color-options`}
                        label={t(TEXTS.SelectLabel.Color)}
                        onChange={(event) => {
                          const { value } = event.target as HTMLSelectElement;
                          const selectedColor = deviceColorOptions?.find(
                            (color) => color.value === value
                          );
                          handleDeviceColorOptionChange(selectedColor);
                        }}
                        options={
                          deviceColorOptions?.map((color) => ({
                            name: color.value,
                            value: color.value,
                          })) || []
                        }
                        value={selectedDeviceColorOption?.value}
                      />
                    </DropdownContainer>
                  )
                )}
              </InputsContainer>
              <ClaimsOffRamp paddingTop="12px" />
            </Container>
          ) : (
            <StepContentSkeleton />
          )}
        </LeftMainContentSection>
        <NavigationButtonsContainer {...navigateButtonProps} />
      </MainContentContainer>
    </PageContainer>
  );
}
